import { Channel, Actions as StreamApiActions } from '../actions/streamApi';

export const CHANNEL_PROTOCOL_CONFIG = {
  STOMP_CHANNELS: [
    Channel.DEVICE_STATUS,
    Channel.SENSOR_READINGS,
    Channel.REPORTS_STATUS,
    Channel.INCIDENT_INFO,
  ],
  WEBSOCKET_CHANNELS: [Channel.PING],
} as const;

export const isStompChannel = (channel: Channel): boolean => {
  return CHANNEL_PROTOCOL_CONFIG.STOMP_CHANNELS.includes(channel as any);
};

export const isWebSocketChannel = (channel: Channel): boolean => {
  return CHANNEL_PROTOCOL_CONFIG.WEBSOCKET_CHANNELS.includes(channel as any);
};

export const WEBSOCKET_ENDPOINTS = {
  STREAM_SERVICE: process.env.REACT_APP_STREAM_SERVICE_URL,
  STOMP_SERVICE: process.env.REACT_APP_STREAM_SERVICE_STOMP_URL,
} as const;

export interface ChannelConfig {
  actionType: StreamApiActions;
  getSubscriptionKey: (data: any) => string;
  validateData: (data: any) => boolean;
  getTopic: (data: any) => string;
  processMessage: (parsedData: any, originalData: any) => any;
  shouldAutoUnsubscribe?: (parsedData: any) => boolean;
}

export const CHANNEL_CONFIG: Partial<Record<Channel, ChannelConfig>> = {
  [Channel.SENSOR_READINGS]: {
    actionType: StreamApiActions.RECEIVED_DEVICE_READING,
    getSubscriptionKey: (data: any) =>
      `${Channel.SENSOR_READINGS}-${data.vendor}-${data.uuid}`,
    validateData: (data: any) => {
      if (!data.vendor || !data.uuid) {
        console.warn(
          'Vendor and UUID are required to subscribe to sensor readings'
        );
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.SENSOR_READINGS}/${data.uuid}/${data.vendor}`,
    processMessage: (parsedData: any, originalData: any) => {
      return {
        type: StreamApiActions.RECEIVED_DEVICE_READING,
        payload: parsedData.data,
      };
    },
  },
  [Channel.DEVICE_STATUS]: {
    actionType: StreamApiActions.RECEIVED_DEVICE_STATUS,
    getSubscriptionKey: (data: any) =>
      `${Channel.DEVICE_STATUS}-${data.vendor}-${data.uuid}`,
    validateData: (data: any) => {
      if (!data.vendor || !data.uuid) {
        console.warn(
          'Vendor and UUID are required to subscribe to device status'
        );
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.DEVICE_STATUS}/${data.uuid}/${data.vendor}`,
    processMessage: (parsedData: any, originalData: any) => {
      return {
        type: StreamApiActions.RECEIVED_DEVICE_STATUS,
        payload: parsedData.data,
      };
    },
  },
  [Channel.REPORTS_STATUS]: {
    actionType: StreamApiActions.RECEIVED_REPORT_STATUS,
    getSubscriptionKey: (data: any) =>
      `${Channel.REPORTS_STATUS}-${data.jobId || 'all'}`,
    validateData: (data: any) => {
      if (!data.jobId) {
        console.warn('Job ID is required to subscribe to reports status');
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.REPORTS_STATUS}/${data.jobId}`,
    processMessage: (parsedData: any, originalData: any) => {
      const { jobId } = originalData;
      return {
        type: StreamApiActions.RECEIVED_REPORT_STATUS,
        payload: {
          ...parsedData.data,
          jobId: jobId || parsedData.jobId,
        },
      };
    },
    shouldAutoUnsubscribe: (parsedData: any) =>
      parsedData?.data?.jobState === 'SUCCESS',
  },
  [Channel.INCIDENT_INFO]: {
    actionType: StreamApiActions.RECEIVED_SUBSCRIBED_INCIDENT,
    getSubscriptionKey: (data: any) =>
      `${Channel.INCIDENT_INFO}-${data.equipmentId || data.spaceId}`,
    validateData: (data: any) => {
      if (!data.equipmentId && !data.spaceId) {
        console.warn('Equipment ID is required to subscribe to incident info');
        return false;
      }
      return true;
    },
    getTopic: (data: any) => {
      if (data.equipmentId) {
        return `/topic/subscribe/${Channel.INCIDENT_INFO}/equipment/${data.equipmentId}`;
      } else if (data.spaceId) {
        return `/topic/subscribe/${Channel.INCIDENT_INFO}/space/${data.spaceId}`;
      }
      return '';
    },
    processMessage: (parsedData: any, originalData: any) => {
      return {
        type: StreamApiActions.RECEIVED_SUBSCRIBED_INCIDENT,
        payload: parsedData.data?.incidentInfoDto || parsedData?.data,
      };
    },
  },
} as const;
